import { useEffect, useRef } from 'react';
import { useTheme } from '../context/ThemeContext';

/**
 * Custom hook for managing TradingView chart widget
 * @param {Object} coin - The coin object
 * @param {string} selectedTimeframe - The selected timeframe
 * @returns {Object} - { chartContainerRef }
 */
export const useTradingViewChart = (coin, selectedTimeframe) => {
  const chartContainerRef = useRef(null);
  const { theme } = useTheme();

  const getDateRange = (timeframe) => {
    const ranges = {
      '1D': '1D',
      '1W': '1W',
      '1M': '1M',
      '1Y': '12M',
      'ALL': '60M'
    };
    return ranges[timeframe] || '1M';
  };

  const getTradingViewSymbol = (coin) => {
    const symbolMap = {
      'BTC': 'BTCUSD',
      'ETH': 'ETHUSD',
      'USDT': 'USDTUSD',
      'USDC': 'USDCUSD',
      'BNB': 'BNBUSD',
      'ADA': 'ADAUSD',
      'SOL': 'SOLUSD',
      'XRP': 'XRPUSD',
      'DOT': 'DOTUSD',
      'DOGE': 'DOGEUSD',
      'AVAX': 'AVAXUSD',
      'SHIB': 'SHIBUSD',
      'MATIC': 'MATICUSD',
      'LTC': 'LTCUSD',
      'UNI': 'UNIUSD',
      'LINK': 'LINKUSD',
      'ATOM': 'ATOMUSD'
    };

    return symbolMap[coin?.symbol?.toUpperCase()] || `${coin?.symbol?.toUpperCase() || 'BTC'}USD`;
  };

  useEffect(() => {
    if (!coin?.symbol || !chartContainerRef.current) return;

    const container = chartContainerRef.current;

    // Clear previous widget
    while (container.firstChild) {
      container.removeChild(container.firstChild);
    }

    const widgetDiv = document.createElement("div");
    widgetDiv.className = "tradingview-widget-container";
    container.appendChild(widgetDiv);

    const widgetInner = document.createElement("div");
    widgetInner.id = `tradingview_${coin.symbol}`;
    widgetDiv.appendChild(widgetInner);

    const script = document.createElement("script");
    script.src = 'https://s3.tradingview.com/external-embedding/embed-widget-symbol-overview.js';
    script.async = true;

    const config = {
      symbols: [[`BINANCE:${getTradingViewSymbol(coin)}|${getDateRange(selectedTimeframe)}`]],
      chartOnly: true,
      width: "100%",
      height: "220",
      locale: "en",
      colorTheme: theme === 'dark' ? 'dark' : 'light',
      autosize: true,
      showVolume: false,
      showMA: false,
      hideDateRanges: true,
      hideMarketStatus: true,
      hideSymbolLogo: true,
      scalePosition: "right",
      scaleMode: "Normal",
      fontFamily: "-apple-system, BlinkMacSystemFont, Trebuchet MS, Roboto, Ubuntu, sans-serif",
      fontSize: "10",
      noTimeScale: false,
      valuesTracking: "1",
      changeMode: "price-and-percent",
      trendLineColor: "rgba(161, 204, 60, 1)", // Using accent color
      underLineColor: "rgba(161, 204, 60, 0.3)",
      underLineBottomColor: "rgba(161, 204, 60, 0)",
      isTransparent: true,
      backgroundColor: "rgba(255, 255, 255, 0)",
      gridLineColor: theme === 'dark' ? "rgba(255, 255, 255, 0.1)" : "rgba(0, 0, 0, 0.1)",
      textColor: theme === 'dark' ? "#848e9c" : "#555555",
    };

    script.innerHTML = JSON.stringify(config);
    widgetDiv.appendChild(script);

    return () => {
      while (container.firstChild) {
        container.removeChild(container.firstChild);
      }
    };
  }, [coin, selectedTimeframe, theme]);

  return {
    chartContainerRef
  };
};